import { DownloadSimpleIcon } from "@phosphor-icons/react";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	downloadDeploymentReportCsv,
	useDeploymentReport,
} from "@/api/media-transmitters/queries";
import type { DeploymentReportRow } from "@/api/media-transmitters/types";
import {
	type Column,
	MeterTable,
	usePagination,
} from "@/components/meters/meter-table";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { Button } from "@/components/ui/button";
import { EmptyCell } from "@/components/ui/empty-cell";
import { useDebounce } from "@/hooks/use-debounce";

function buildColumns(t: TFunction): Column<DeploymentReportRow>[] {
	return [
		{
			key: "sn",
			label: t("deploymentReport.colSn"),
			sortField: "sn",
			render: (row) => (
				<CopyableCell value={row.sn} label={t("deploymentReport.colSn")} />
			),
		},
		{
			key: "meter_sn",
			label: t("deploymentReport.colMeterSn"),
			render: (row) =>
				row.meter_sn ? (
					<CopyableCell
						value={row.meter_sn}
						label={t("deploymentReport.colMeterSn")}
					/>
				) : (
					<EmptyCell />
				),
		},
		{
			key: "manufacturer",
			label: t("deploymentReport.colManufacturer"),
			sortField: "manufacturer",
			render: (row) => row.manufacturer ?? <EmptyCell />,
		},
		{
			key: "frames",
			label: t("deploymentReport.colFrames"),
			sortField: "frames",
			render: (row) => row.frames,
		},
	];
}

export function DeploymentReportTable() {
	const { t } = useTranslation();

	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const [ordering, setOrdering] = useState("");
	const [search, setSearch] = useState("");
	const [isExporting, setIsExporting] = useState(false);
	const debouncedSearch = useDebounce(search);

	const { data, isLoading, isError, refetch } = useDeploymentReport({
		page,
		page_size: pageSize,
		ordering: ordering || undefined,
		search: debouncedSearch || undefined,
	});

	async function handleExport() {
		setIsExporting(true);
		try {
			await downloadDeploymentReportCsv({
				ordering: ordering || undefined,
				search: debouncedSearch || undefined,
			});
		} catch {
			toast.error(t("deploymentReport.exportError"));
		} finally {
			setIsExporting(false);
		}
	}

	return (
		<MeterTable
			data={data}
			columns={buildColumns(t)}
			loading={isLoading}
			isError={isError}
			onRetry={() => refetch()}
			page={page}
			pageSize={pageSize}
			onPageChange={(p) => setPage(p)}
			onPageSizeChange={(s) => {
				setPageSize(s);
				setPage(1);
			}}
			search={search}
			onSearchChange={(s) => {
				setSearch(s);
				setPage(1);
			}}
			searchPlaceholder={t("deploymentReport.searchPlaceholder")}
			ordering={ordering}
			onOrderingChange={(o) => {
				setOrdering(o);
				setPage(1);
			}}
			rowKeyField="sn"
			rowKey={(row) => `${row.sn}|${row.meter_sn ?? ""}`}
			headerActions={
				<div className="flex justify-end">
					<Button
						variant="outline"
						size="sm"
						onClick={handleExport}
						disabled={isExporting}
					>
						<DownloadSimpleIcon aria-hidden="true" className="mr-2 h-4 w-4" />
						{t("deploymentReport.exportCsv")}
					</Button>
				</div>
			}
		/>
	);
}
